import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, TRY, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, CheckCircle2, Clock, CreditCard, History, AlertTriangle } from "lucide-react";

const thisMonth = new Date().toISOString().slice(0, 7);
const thisYear = String(new Date().getFullYear());
const empty = { facility: "", period_type: "monthly", period: thisMonth, amount: "", paid: false, note: "" };

function isOverdue(p) {
  if (p.paid) return false;
  if (p.period_type === "monthly") return p.period < thisMonth;
  return String(p.period) < thisYear;
}

export default function Payments() {
  const [items, setItems] = useState([]);
  const [owners, setOwners] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("all"); // all | monthly | yearly
  const [historyFacility, setHistoryFacility] = useState(null);

  const load = () => api.get("/payments").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/field-owners").then((r) => setOwners(r.data));
  }, []);

  const openNew = () => {
    setForm(empty);
    setOpen(true);
  };

  const save = async () => {
    if (!form.facility) return toast.error("Tesis seçin");
    if (!form.period) return toast.error("Dönem girin");
    try {
      await api.post("/payments", { ...form, amount: parseFloat(form.amount) || 0 });
      toast.success("Ödeme kaydı eklendi");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const togglePaid = async (p) => {
    await api.put(`/payments/${p.id}`, { ...p, paid: !p.paid });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/payments/${id}`);
    toast.success("Silindi");
    load();
  };

  const filtered = items.filter((p) => filter === "all" || p.period_type === filter);
  const paidCount = filtered.filter((p) => p.paid).length;
  const pendingAmount = filtered.filter((p) => !p.paid).reduce((s, p) => s + Number(p.amount || 0), 0);
  const overdue = items.filter(isOverdue);
  const overdueAmount = overdue.reduce((s, p) => s + Number(p.amount || 0), 0);
  const historyItems = historyFacility
    ? items
        .filter((p) => p.facility === historyFacility)
        .sort((a, b) => String(b.period).localeCompare(String(a.period)))
    : [];

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.3em] text-primary mb-2">Takip</p>
          <h1 className="font-anton text-4xl sm:text-5xl">SAHA ÖDEMELERİ</h1>
          <p className="text-muted-foreground mt-2">Sahaların aylık / yıllık ödeme durumunu takip edin.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-payment-button" onClick={openNew} className="bg-primary hover:bg-primary/90 text-white">
              <Plus className="w-4 h-4 mr-1" /> Ödeme Ekle
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-anton text-2xl">YENİ ÖDEME KAYDI</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Tesis</Label>
                {owners.length > 0 ? (
                  <Select value={form.facility} onValueChange={(v) => setForm({ ...form, facility: v })}>
                    <SelectTrigger data-testid="payment-facility-select"><SelectValue placeholder="Tesis seç" /></SelectTrigger>
                    <SelectContent>
                      {owners.map((o) => (
                        <SelectItem key={o.id} value={o.facility_name}>{o.facility_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm text-muted-foreground">Önce <b>Saha Sahipleri</b> sayfasından tesis ekleyin.</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Ödeme Türü</Label>
                  <Select
                    value={form.period_type}
                    onValueChange={(v) => setForm({ ...form, period_type: v, period: v === "monthly" ? thisMonth : thisYear })}
                  >
                    <SelectTrigger data-testid="payment-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Aylık</SelectItem>
                      <SelectItem value="yearly">Yıllık</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Dönem</Label>
                  {form.period_type === "monthly" ? (
                    <Input type="month" data-testid="payment-period-input" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} />
                  ) : (
                    <Input type="number" data-testid="payment-period-input" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} placeholder="2026" />
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Tutar (₺)</Label>
                <Input type="number" data-testid="payment-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0" />
              </div>
              <div className="space-y-2">
                <Label>Durum</Label>
                <Select value={form.paid ? "paid" : "pending"} onValueChange={(v) => setForm({ ...form, paid: v === "paid" })}>
                  <SelectTrigger data-testid="payment-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Ödenmedi</SelectItem>
                    <SelectItem value="paid">Ödendi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Not</Label>
                <Input data-testid="payment-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="İsteğe bağlı" />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="save-payment-button" onClick={save} className="bg-primary hover:bg-primary/90 text-white">Kaydet</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {overdue.length > 0 && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} data-testid="overdue-banner">
          <Card className="bg-destructive/10 border-destructive/40 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 rounded-md bg-destructive/20 text-destructive flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold text-destructive">{overdue.length} saha ödemesi gecikti!</p>
                <p className="text-sm text-muted-foreground">
                  Toplam bekleyen gecikmiş tutar: <b className="text-destructive">{TRY(overdueAmount)}</b>
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {overdue.slice(0, 5).map((p) => (
                <Badge key={p.id} variant="outline" className="border-destructive/40 text-destructive text-xs">
                  {p.facility} · {p.period}
                </Badge>
              ))}
              {overdue.length > 5 && <span className="text-xs text-muted-foreground self-center">+{overdue.length - 5}</span>}
            </div>
          </Card>
        </motion.div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="bg-secondary">
            <TabsTrigger value="all">Tümü</TabsTrigger>
            <TabsTrigger value="monthly">Aylık</TabsTrigger>
            <TabsTrigger value="yearly">Yıllık</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex gap-3 text-sm">
          <span className="text-primary font-semibold">Ödenen: {paidCount}/{filtered.length}</span>
          <span className="text-destructive font-semibold">Bekleyen: {TRY(pendingAmount)}</span>
        </div>
      </div>

      {filtered.length === 0 && (
        <Card className="bg-card border-border p-10 text-center text-muted-foreground">Henüz ödeme kaydı yok.</Card>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="payments-list">
        {filtered.map((p, i) => (
          <motion.div key={p.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
            <Card className={`p-5 border h-full flex flex-col ${isOverdue(p) ? "bg-destructive/10 border-destructive/50" : p.paid ? "bg-primary/5 border-primary/40" : "bg-card border-border"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="w-10 h-10 rounded-md bg-secondary flex items-center justify-center shrink-0">
                  <CreditCard className="w-5 h-5 text-primary" />
                </div>
                <div className="flex items-center gap-1">
                  <button data-testid={`history-payment-${p.id}`} onClick={() => setHistoryFacility(p.facility)} title="Geçmişi gör" className="opacity-50 hover:opacity-100 hover:text-primary p-1">
                    <History className="w-4 h-4" />
                  </button>
                  <button data-testid={`delete-payment-${p.id}`} onClick={() => remove(p.id)} className="opacity-40 hover:opacity-100 hover:text-destructive p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <h3 className="font-anton text-xl">{p.facility}</h3>
                {isOverdue(p) && (
                  <Badge className="bg-destructive text-white text-[11px] gap-1" data-testid={`overdue-badge-${p.id}`}>
                    <AlertTriangle className="w-3 h-3" /> GECİKTİ
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="border-border text-xs">{p.period_type === "monthly" ? "Aylık" : "Yıllık"}</Badge>
                <span className="text-sm text-muted-foreground">{p.period}</span>
              </div>
              <p className="font-anton text-2xl mt-3">{TRY(p.amount)}</p>
              {p.note && <p className="text-xs text-muted-foreground mt-1">{p.note}</p>}
              <button
                data-testid={`toggle-payment-${p.id}`}
                onClick={() => togglePaid(p)}
                className={`mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition-colors ${p.paid ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"}`}
              >
                {p.paid ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                {p.paid ? "Ödendi" : "Ödenmedi"} · değiştir
              </button>
            </Card>
          </motion.div>
        ))}
      </div>

      <Dialog open={!!historyFacility} onOpenChange={(o) => !o && setHistoryFacility(null)}>
        <DialogContent className="bg-card border-border max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-anton text-2xl flex items-center gap-2">
              <History className="w-5 h-5 text-primary" /> {historyFacility} — ÖDEME GEÇMİŞİ
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2" data-testid="payment-history-list">
            {historyItems.length === 0 && <p className="text-sm text-muted-foreground py-4">Kayıt yok.</p>}
            {historyItems.map((p) => (
              <div key={p.id} className={`flex items-center gap-3 p-3 rounded-md border ${isOverdue(p) ? "border-destructive/40 bg-destructive/5" : "border-border bg-secondary/30"}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{p.period}</span>
                    <Badge variant="outline" className="border-border text-[10px]">{p.period_type === "monthly" ? "Aylık" : "Yıllık"}</Badge>
                    {isOverdue(p) && <Badge className="bg-destructive text-white text-[10px]">Gecikti</Badge>}
                  </div>
                  {p.note && <p className="text-xs text-muted-foreground mt-0.5">{p.note}</p>}
                </div>
                <span className="font-anton text-lg">{TRY(p.amount)}</span>
                <Badge variant="outline" className={p.paid ? "border-primary/40 text-primary" : "border-destructive/40 text-destructive"}>
                  {p.paid ? "Ödendi" : "Ödenmedi"}
                </Badge>
              </div>
            ))}
          </div>
          {historyItems.length > 0 && (
            <div className="flex items-center justify-between pt-2 border-t border-border text-sm">
              <span className="text-muted-foreground">Toplam ödenen</span>
              <span className="font-anton text-lg text-primary">
                {TRY(historyItems.filter((p) => p.paid).reduce((s, p) => s + Number(p.amount || 0), 0))}
              </span>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
